<template>
  <div class="faq-page">
    <div class="modern-header">
      <div class="header-content">
        <img
          src="../assets/images/logo/good_casper_white.png"
          alt="Good Casper logo"
          class="logo-image"
          @click="$router.push('/')"
          role="link"
        />
        <div class="buttons-header">
          <v-btn text to="/Login?state=login" class="nav-btn">
            Login
          </v-btn>
          <v-btn text to="/Login?state=signup" class="nav-btn">
            Sign Up
          </v-btn>
          <v-btn text to="/demo/Profile" class="nav-btn">
            Demo
          </v-btn>
        </div>
      </div>
    </div>

    <div class="faq-hero">
      <h1 class="hero-title">Frequently Asked Questions</h1>
      <p class="hero-subtitle">
        Find answers to common questions about Good Casper
      </p>
    </div>

    <v-container class="faq-container">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <div class="faq-grid">
            <div 
              v-for="(faq, index) in FAQs" 
              :key="index"
              class="faq-card"
              :class="{ 'active': activeIndex === index }"
              @click="toggleFaq(index)"
            >
              <div class="faq-card-header">
                <div class="d-flex align-center">
                  <v-icon class="mr-4" :color="activeIndex === index ? '#4ECDC4' : '#FF6B6B'">
                    mdi-help-circle-outline
                  </v-icon>
                  <h3>{{ faq.title }}</h3>
                </div>
                <v-icon class="toggle-icon" :class="{ 'rotated': activeIndex === index }">
                  mdi-chevron-down
                </v-icon>
              </div>
              <div class="faq-card-content" :class="{ 'expanded': activeIndex === index }">
                <div v-html="faq.text" class="faq-text"></div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  data() {
    return {
      activeIndex: null
    };
  },
  mounted() {
    document.title = "Good Casper - FAQ";
    document.documentElement.classList.add('faq-page-active');
    document.body.classList.add('faq-page-active');
  },
  beforeDestroy() {
    document.documentElement.classList.remove('faq-page-active');
    document.body.classList.remove('faq-page-active');
  },
  computed: {
    lang() {
      return this.$store.state.langObj || {};
    },
    FAQs() {
      return [
        {
          title: "What is GOOD CASPER?",
          text: "GOOD CASPER is a heartfelt application designed to help you create a lasting memory of your deceased loved ones, whether they are humans or pets. <br><br>It provides a platform for users to post last greetings, share memories, create timelines of their lives, upload images, videos, documents, and other information that celebrates and honors their life journey.",
        },
        {
          title: "General Information",
          text: "<a target='_blank' href='/TermsAndConditions'>The Terms and Conditions</a> govern the rights and obligations of users and the service provider on the GOOD CASPER platform. Please read this document carefully before accessing or using the platform. By using the platform, you agree to the stated terms.",
        },
        {
          title: "Is GOOD CASPER free to use?",
          text: "<strong>Special Offer</strong>: The <strong>first 1000 profiles</strong> created on GOOD CASPER will be completely <strong>FREE FOREVER</strong> with all premium features unlocked - equivalent to our highest package!<br><br>After the first 1000 profiles, GOOD CASPER offers a <strong>free version</strong> with basic functionality and features, as well as <strong>package subscription</strong> options that unlock additional advanced features and capabilities.",
        },
        {
          title: "Registration and Use",
          text: "Registration on the platform is mandatory to access all functionalities. Users must provide accurate and complete information during registration.<br><br>User accounts are personal and cannot be shared with third parties.<br><br>Users are responsible for maintaining the confidentiality of their login credentials.<br><br>The platform reserves the right to suspend or terminate an account in case of a violation of rules.",
        },
        {
          title: "Do I need to enter all information when creating a profile?",
          text: "No, you don't have to enter all the information right away when creating the profile. Each field can be edited at any time, allowing you the flexibility to add or update details whenever you need.",
        },
        {
          title: "Who can post Last Greetings?",
          text: "The owner of the profile and the profile followers can post Last Greetings.<br>You can also edit or delete any Last Greetings you have posted.",
        },
        {
          title: "Can I upload images, videos, and documents on GOOD CASPER?",
          text: "GOOD CASPER offers dedicated sections for profile owners of deceased persons or pets. These sections allow uploading images, videos, and documents, while followers can actively participate in preserving memories through comments, threads, likes, and bookmarking their favorite content.",
        },
        {
          title: "Can I create a timeline of a deceased person or pet's life?",
          text: "Absolutely!<br>GOOD CASPER allows you to create a timeline that beautifully captures the life journey of your deceased loved one.<br>You can add significant events, milestones, photos, videos that represent their life, creating a cherished digital memory.",
        },
        {
          title: "What is the purpose of the 'Missing Puzzle' feature?",
          text: "The 'Missing Puzzle' section is a thoughtful feature designed to enrich the memory of the deceased. It allows followers to upload images that the profile owner might not have, helping to fill in the gaps and create a more complete picture of the person's or pet's life. These contributions offer unique perspectives and cherished moments that may otherwise remain untold. <br><br>The profile owner retains full control, deciding which images to include on the profile, ensuring that every addition aligns with the intended tribute.",
        },
        {
          title: "Can I report a profile or post on GOOD CASPER?",
          text: "The report feature is available for the profiles, last greetings, comments, threads, images or videos.",
        },
        {
          title: "How do I contact GOOD CASPER support?",
          text: "If you need assistance or have any questions or concerns regarding GOOD CASPER, you can send an email to support@goodcasper.com<br>We strive to provide prompt and empathetic support to our users during this sensitive time.",
        },
      ];
    },
  },
  methods: {
    toggleFaq(index) {
      this.activeIndex = this.activeIndex === index ? null : index;
    }
  },
};
</script>

<style lang="scss">
/* Global scrollbar styles */
html.faq-page-active,
body.faq-page-active {
  &::-webkit-scrollbar {
    width: 8px;
    background: #1a1a1a;
  }

  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 4px;
    
    &:hover {
      background: #444;
    }
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
  }
}

.faq-page {
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  min-height: 100vh;
  color: #fff;
  font-family: 'Montserrat', sans-serif;
}

.modern-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: transparent;
  padding: 1rem 2rem;
  transition: all 0.3s ease;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 1400px;
    margin: 0 auto;
  }

  .logo-image {
    width: 200px;
    transition: transform 0.3s ease;
    cursor: pointer;
    &:hover {
      transform: scale(1.05);
    }
  }

  .buttons-header {
    display: flex;
    gap: 1.5rem;
    align-items: center;

    .nav-btn {
      color: rgba(255, 255, 255, 0.9) !important;
      font-family: 'Montserrat', sans-serif !important;
      font-weight: 500 !important;
      font-size: 0.95rem !important;
      letter-spacing: 0.3px !important;
      text-transform: none !important;
      padding: 0.5rem 1.25rem !important;
      border-radius: 8px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      
      &:hover {
        color: #fff !important;
        background: rgba(255, 255, 255, 0.08) !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
      }
    }
  }
}

.faq-hero {
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
    url("../assets/images/faq/faq.jpg");
  background-size: cover;
  background-position: center;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  padding: 0 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255, 107, 107, 0.2), rgba(78, 205, 196, 0.2));
    z-index: 1;
  }

  .hero-title {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 900;
    margin-bottom: 1.5rem;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
    position: relative;
    z-index: 2;
    background: linear-gradient(45deg, #FF6B6B, #4ECDC4);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .hero-subtitle {
    font-size: clamp(1.2rem, 2vw, 1.5rem);
    opacity: 0.9;
    max-width: 600px;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
    position: relative;
    z-index: 2;
  }
}

.faq-container {
  margin-top: 0;
  padding: 80px 0;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
}

.faq-grid {
  display: grid;
  gap: 1.5rem;
}

.faq-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.2);
  }

  &.active {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.3);
  }
}

.faq-card-header {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: rgba(255, 255, 255, 0.95);
  }

  .toggle-icon {
    transition: transform 0.3s ease;
    color: rgba(255, 255, 255, 0.7);

    &.rotated {
      transform: rotate(180deg);
    }
  }
}

.faq-card-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
  padding: 0 1.5rem;

  &.expanded {
    max-height: 1000px;
    padding-bottom: 1.5rem;
  }
}

.faq-text {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  font-size: 1rem;

  a {
    color: #4ECDC4;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #FF6B6B;
    }
  }

  strong {
    color: #fff;
  }
}

@media (max-width: 768px) {
  .modern-header {
    padding: 0.75rem 1rem;
    
    .header-content {
      flex-direction: column;
      gap: 1rem;
    }

    .logo-image {
      width: 150px;
    }

    .buttons-header {
      width: 100%;
      justify-content: center;
      flex-wrap: wrap;
      gap: 0.75rem;
      
      .nav-btn {
        font-size: 0.85rem !important;
        padding: 0.4rem 0.75rem !important;
      }
    }
  }

  .faq-hero {
    height: 60vh;
    padding: 0 20px;

    .hero-title {
      font-size: clamp(1.8rem, 4vw, 2.2rem) !important;
      margin-bottom: 0.75rem;
    }

    .hero-subtitle {
      font-size: clamp(0.9rem, 1.5vw, 1.1rem) !important;
      max-width: 90%;
      margin: 0 auto;
    }
  }

  .faq-container {
    margin-top: 0;
    padding: 20px 0;
  }

  .faq-card-header {
    padding: 1.25rem;

    h3 {
      font-size: 1rem;
    }
  }
}
</style>